import { useEffect } from 'react';
import { fabric } from 'fabric';

const useObjectModifications = (canvas, images, currentImageIndex, updateAnnotations, updatePolygons, getCurrentImage, saveState) => {
  useEffect(() => {
    if (!canvas) return;

    let savedBeforeModify = false;

    const onModifyStart = () => {
      if (!savedBeforeModify) {
        saveState?.();
        savedBeforeModify = true;
      }
    };

    const onObjectModified = (o) => {
      savedBeforeModify = false;
      if (!o.target) return;
      const currentImage = getCurrentImage();
      if (!currentImage) return;

      const target = o.target;
      const idx = target.data?.annIndex ?? target.data?.polyIndex;
      if (idx === undefined) return;

      if (target.type === 'rect') {
        const width = target.width * target.scaleX / currentImage.originalWidth;
        const height = target.height * target.scaleY / currentImage.originalHeight;
        const centerX = (target.left + target.width * target.scaleX / 2) / currentImage.originalWidth;
        const centerY = (target.top + target.height * target.scaleY / 2) / currentImage.originalHeight;
        const angle = target.angle || 0;
        const updatedAnnotations = [...currentImage.annotations];
        if (updatedAnnotations[idx]) {
          const wasAiSuggestion = !!updatedAnnotations[idx].isAI;
          updatedAnnotations[idx] = {
            ...updatedAnnotations[idx],
            width,
            height,
            centerX,
            centerY,
            angle,
            // editing an AI suggestion counts as accepting it
            isAI: false,
          };
          updateAnnotations(updatedAnnotations);

          if (wasAiSuggestion) {
            target.set({ strokeDashArray: null, fill: 'transparent' });
            if (target.data) target.data.isAI = false;
          }
        }
      }
      else if (target.type === 'polygon') {
        // Get the actual points from the polygon
        const points = target.get('points');

        // Calculate the actual coordinates on canvas
        const actualPoints = points.map(p => {
          // Transform point from local coordinates to canvas coordinates
          return fabric.util.transformPoint(
            new fabric.Point(p.x - target.pathOffset.x, p.y - target.pathOffset.y),
            target.calcTransformMatrix()
          );
        });

        // Normalize points to original image dimensions
        const normalizedPoints = actualPoints.map(p => ({
          x: p.x / currentImage.originalWidth,
          y: p.y / currentImage.originalHeight
        }));

        const updatedPolygons = [...currentImage.polygons];
        if (updatedPolygons[idx]) {
          updatedPolygons[idx] = {
            ...updatedPolygons[idx],
            points: normalizedPoints
          };
          updatePolygons(updatedPolygons);
        }
      }

      canvas.requestRenderAll();
    };

    canvas.on('object:moving', onModifyStart);
    canvas.on('object:scaling', onModifyStart);
    canvas.on('object:rotating', onModifyStart);
    canvas.on('object:modified', onObjectModified);

    return () => {
      canvas.off('object:moving', onModifyStart);
      canvas.off('object:scaling', onModifyStart);
      canvas.off('object:rotating', onModifyStart);
      canvas.off('object:modified', onObjectModified);
    };
  }, [canvas, images, currentImageIndex, getCurrentImage, updateAnnotations, updatePolygons, saveState]);
};

export default useObjectModifications;